import { CUES, type CueLayer, type CueName } from "./cues";
import { settings } from "../app/settingsStore";

/**
 * Plays cues. One AudioContext, one master gain, no assets.
 *
 * Three things make this harder than "call `play()`", and all three are why
 * this is a module rather than a few lines in a component:
 *
 *  1. **Autoplay policy.** Every browser refuses to start audio before the
 *     user has interacted with the page. The context is therefore created
 *     lazily on the first gesture, and every call before that is a no-op
 *     rather than an exception — a silent first second is fine, a console
 *     full of `NotAllowedError` is not.
 *  2. **Density.** A tick can emit a dozen `packet.moved` events at once.
 *     Twelve copies of a blip inside one frame is not twelve times the
 *     information; it is a buzz, and worse, it sums to twelve times the
 *     amplitude and clips. Cues are throttled per name per window.
 *  3. **Teardown.** Every note builds its own nodes, so they must disconnect
 *     themselves when they finish or a long shift leaks a few thousand
 *     oscillators.
 *
 * The graph for one layer is built by `scheduleLayer`, which takes its context
 * and destination as arguments rather than reaching for the module's engine.
 * That is not abstraction for its own sake: it is what lets a test render a
 * cue through an OfflineAudioContext and assert that a sound actually comes
 * out. The first version of this file built the whole graph correctly and
 * never connected the source to it, so every cue was scheduled, started,
 * stopped — and inaudible. Nothing that checked types, registries or context
 * state could see that; only listening to the output can.
 */

/** How wide the anti-buzz window is. Roughly three frames. */
const THROTTLE_WINDOW_MS = 55;
/** A hard ceiling on notes in flight, whatever the per-cue limits allow. */
const MAX_VOICES = 24;
/** Seconds of white noise in the shared buffer. */
const NOISE_SECONDS = 2;

interface Engine {
  ctx: AudioContext;
  master: GainNode;
  /** One shared noise buffer; generating it per hit is pure waste. */
  noise: AudioBuffer;
}

let engine: Engine | null = null;
let failed = false;
let voices = 0;

const recent = new Map<CueName, { windowStart: number; count: number }>();

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** White noise, reused by every noise layer from a random offset. */
export function buildNoiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * NOISE_SECONDS);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/**
 * Starts audio. Safe to call repeatedly and from any gesture handler; the
 * second and later calls only nudge a suspended context back to life, which
 * is what a tab regaining focus needs.
 */
export function unlockAudio(): void {
  if (failed) return;
  if (engine) {
    if (engine.ctx.state === "suspended") void engine.ctx.resume();
    return;
  }

  const Ctor = audioContextCtor();
  if (!Ctor) {
    failed = true;
    return;
  }

  try {
    const ctx = new Ctor();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    engine = { ctx, master, noise: buildNoiseBuffer(ctx) };
    applyVolume();
    if (ctx.state === "suspended") void ctx.resume();
  } catch {
    // No audio hardware, or a context limit. The game is fully playable
    // without sound, so this is not worth surfacing.
    failed = true;
  }
}

/** Pushes the current mute/volume settings at the master gain. */
export function applyVolume(): void {
  if (!engine) return;
  const { muted, volume } = settings();
  const target = muted ? 0 : volume;
  // A ramp rather than a jump: setting a gain instantly clicks, which is a
  // silly noise to make while someone is turning the sound down.
  engine.master.gain.setTargetAtTime(target, engine.ctx.currentTime, 0.02);
}

/** True when this cue has already had its allowance this window. */
function throttled(name: CueName, max: number): boolean {
  const now = performance.now();
  const entry = recent.get(name);
  if (!entry || now - entry.windowStart > THROTTLE_WINDOW_MS) {
    recent.set(name, { windowStart: now, count: 1 });
    return false;
  }
  entry.count += 1;
  return entry.count > max;
}

/**
 * Builds and starts one layer: source → envelope → (filter) → destination.
 *
 * `onDone` is how the caller reclaims a voice; an offline render has no voice
 * budget and passes nothing.
 */
export function scheduleLayer(
  ctx: BaseAudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  layer: CueLayer,
  startAt: number,
  level: number,
  onDone?: () => void,
): void {
  const attack = layer.attack ?? 0.002;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(layer.gain * level, startAt + attack);
  // Exponential decay to a floor, then an explicit zero: ramping to exactly
  // zero exponentially is undefined, and the floor is inaudible anyway.
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + layer.duration);
  gain.gain.setValueAtTime(0, startAt + layer.duration);

  let tail: AudioNode = gain;
  if (layer.filter) {
    const filter = ctx.createBiquadFilter();
    filter.type = layer.filter.type;
    filter.frequency.value = layer.filter.freq;
    if (layer.filter.q !== undefined) filter.Q.value = layer.filter.q;
    gain.connect(filter);
    tail = filter;
  }
  tail.connect(destination);

  // Branching on the layer's own kind rather than on `instanceof`: the node
  // classes are globals that need not exist wherever this runs, and the data
  // already says which one it wants.
  let source: AudioScheduledSourceNode;
  if (layer.kind === "tone") {
    const osc = ctx.createOscillator();
    osc.type = layer.wave ?? "sine";
    osc.frequency.setValueAtTime(layer.freq, startAt);
    if (layer.freqEnd !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(layer.freqEnd, 1),
        startAt + layer.duration,
      );
    }
    source = osc;
  } else {
    const buffered = ctx.createBufferSource();
    buffered.buffer = noise;
    buffered.loop = true;
    source = buffered;
  }

  // THE line. Without it everything above is a correctly-built graph with
  // nothing flowing into it, which is exactly as silent as building nothing.
  source.connect(gain);

  source.onended = () => {
    onDone?.();
    source.disconnect();
    gain.disconnect();
    if (tail !== gain) tail.disconnect();
  };

  // Noise sources read from a random point so repeated hits are not identical.
  if (layer.kind === "noise") {
    (source as AudioBufferSourceNode).start(startAt, Math.random() * (NOISE_SECONDS - 0.5));
  } else {
    source.start(startAt);
  }
  source.stop(startAt + layer.duration + 0.02);
}

/**
 * Every layer of a cue, with its repeats and delays resolved to start times.
 * Returns how many notes it started, which is what the voice budget counts —
 * a klaxon with two blasts is two notes, not one.
 */
export function scheduleCue(
  ctx: BaseAudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  name: CueName,
  startAt: number,
  onDone?: () => void,
): number {
  const spec = CUES[name];
  const level = spec.level ?? 1;
  let notes = 0;

  for (const layer of spec.layers) {
    const repeats = Math.max(1, layer.repeats ?? 1);
    const gap = layer.repeatGap ?? layer.duration;
    for (let i = 0; i < repeats; i++) {
      scheduleLayer(ctx, destination, noise, layer, startAt + (layer.delay ?? 0) + i * gap, level, onDone);
      notes += 1;
    }
  }
  return notes;
}

/** How long a cue occupies the bus, for sizing an offline render. */
export function cueSeconds(name: CueName): number {
  return CUES[name].layers.reduce((longest, layer) => {
    const repeats = Math.max(1, layer.repeats ?? 1);
    const gap = layer.repeatGap ?? layer.duration;
    return Math.max(longest, (layer.delay ?? 0) + (repeats - 1) * gap + layer.duration);
  }, 0);
}

/**
 * Plays a cue. No-ops before the first gesture, while muted, or when the cue
 * has already sounded its allowance in this window.
 */
export function playCue(name: CueName): void {
  const e = engine;
  if (!e || failed) return;
  if (settings().muted) return;
  if (e.ctx.state === "closed") return;
  // A context that exists but is still suspended is normal for a beat: the
  // gesture that created it fired `resume()`, which is a promise, and the
  // click handler that wants a sound runs first. Scheduling anyway means the
  // note is waiting when the context starts, so the very first thing a player
  // clicks is audible instead of being the one action that makes no noise.
  if (e.ctx.state === "suspended") void e.ctx.resume();

  const spec = CUES[name];
  if (throttled(name, spec.maxPerWindow ?? 8)) return;
  if (voices >= MAX_VOICES) return;

  // Each note reclaims itself, so the budget is incremented by the number
  // actually started. No note can end before this call returns.
  voices += scheduleCue(e.ctx, e.master, e.noise, name, e.ctx.currentTime, () => {
    voices = Math.max(0, voices - 1);
  });
}

/** Test/diagnostic hook — the tuning console reports whether audio came up. */
export function audioStatus(): "off" | "ready" | "unavailable" {
  if (failed) return "unavailable";
  if (!engine) return "off";
  return engine.ctx.state === "running" ? "ready" : "off";
}
